import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Landmark, Loader2, RefreshCw, ExternalLink, CheckCircle2 } from 'lucide-react';
import { CompanyStatusBadge } from '../CompanyStatusBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const KboStatusCard = ({ user, onUpdated }) => {
  const [loading, setLoading] = useState(false);
  const kbo = user?.kbo;
  const isBelgian = (user?.vat_number || '').toUpperCase().startsWith('BE') || !!kbo;
  if (!isBelgian) return null;

  const recheck = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/kbo/verify-me`, {}, { withCredentials: true });
      onUpdated?.({ ...user, kbo: res.data.kbo, kbo_status: res.data.kbo_status, kbo_checked_at: new Date().toISOString() });
      toast.success(res.data.changes?.length ? `${res.data.changes.length} changement(s) détecté(s)` : 'Données BCE à jour');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Vérification impossible');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="kbo-status-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-lg flex items-center gap-2"><Landmark className="w-5 h-5 text-[#1E3A8A]" /> Registre officiel (BCE)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {kbo ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-[#111827]">{kbo.name}</span>
              <span className="font-mono text-xs text-[#6B7280]">{kbo.formatted_number}</span>
              {user.kbo_status?.level === 'none' || !user.kbo_status ? (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"><CheckCircle2 className="w-3 h-3" /> {kbo.status} — {kbo.juridical_situation}</span>
              ) : (
                <CompanyStatusBadge status={user.kbo_status} />
              )}
            </div>
            <p className="text-[#4B5563]">{kbo.address}{kbo.legal_form ? ` · ${kbo.legal_form}` : ''}</p>
            {kbo.nace_codes?.length > 0 && (
              <p className="text-xs text-[#6B7280]">NACE-BEL : {kbo.nace_codes.map((n) => `${n.code} ${n.label}`).join(' · ')}</p>
            )}
            <p className="text-xs text-[#9CA3AF]">Dernière vérification : {user.kbo_checked_at ? new Date(user.kbo_checked_at).toLocaleString('fr-BE') : '—'} · contrôle automatique les 15 et 30 de chaque mois</p>
          </>
        ) : (
          <p className="text-[#4B5563]">Votre numéro d'entreprise n'a pas encore été vérifié auprès de la BCE.</p>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button type="button" size="sm" variant="outline" onClick={recheck} disabled={loading} data-testid="kbo-recheck-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Vérifier maintenant
          </Button>
          {kbo?.source_url && (
            <a href={kbo.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#1E3A8A] hover:underline px-2 py-1.5" data-testid="kbo-source-link">
              <ExternalLink className="w-3 h-3" /> Fiche BCE
            </a>
          )}
          <a href="https://www.regsol.be/RegSol/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#1E3A8A] hover:underline px-2 py-1.5" data-testid="regsol-link">
            <ExternalLink className="w-3 h-3" /> Vérifier sur RegSol
          </a>
        </div>
      </CardContent>
    </Card>
  );
};
